/**
 * Lo que falta del mes, y en cada cosa, el sitio donde soltar su papel.
 *
 * UNA LISTA, NO UN AVISO. Cada fila es algo concreto —«la comunidad de Zabalbide no ha
 * llegado», «el recibo de Iberdrola del 18 no tiene ticket», «Iker no ha pagado agosto»— y al
 * lado lleva la casilla donde se suelta exactamente su ticket o su justificante. Soltarlo ahí
 * no adivina qué es: ya se sabe, porque se ha soltado en su sitio.
 *
 * SE SIGUE PUDIENDO SUBIR CON EL MES CONCILIADO. Lo que llega tarde se suelta igual, y el mes
 * vuelve a «incompleto» para que alguien lo repase y lo concilie de nuevo; lo que no se puede
 * es cambiar lo ya cerrado sin reabrirlo.
 *
 * El orden es el del trabajo: primero cada inmueble con lo suyo, y al final el banco, que es lo
 * que se mira después de subir el extracto.
 */
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BankMovement, LedgerEntry, LedgerGap, LedgerMonthResponse } from '@naar/shared';
import { MONTH_NAMES_ES } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Card, CardHeader } from '../../components/Card';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import { formatEurCents, formatShortDate } from '../../lib/format';
import { FileDrop } from './FileDrop';
import styles from './Ledger.module.css';
import { conGenerales } from './reconciliation';

const ETIQUETA: Record<LedgerGap['reason'], string> = {
  sin_apunte: 'No ha llegado',
  sin_factura: 'Sin ticket',
  sin_confirmar: 'Sin revisar',
  sin_cobrar: 'Sin cobrar',
};

const AYUDA: Record<LedgerGap['reason'], string> = {
  sin_apunte: 'Es un gasto fijo del inmueble que tocaba este mes y del que no hay nada.',
  sin_factura: 'El gasto está apuntado, pero falta el papel que lo respalda.',
  sin_confirmar: 'Lo ha rellenado la aplicación al leer el ticket. Repásalo y confírmalo.',
  sin_cobrar: 'La renta de este mes no ha entrado ni por el banco ni registrada fuera de él.',
};

interface MissingPanelProps {
  month: string;
  data: LedgerMonthResponse;
  canManage: boolean;
  onReviewEntry: (entry: LedgerEntry, propertyName: string) => void;
  onResolveMovement: (movement: BankMovement) => void;
  /** Soltar el ticket de un cargo del banco: falta saber de qué inmueble es. */
  onMovementTicket: (movement: BankMovement, file: File) => void;
  /** Registrar un cobro fuera del banco, con el justificante soltado o sin él. */
  onOffBankPayment: (gap: LedgerGap, propertyName: string, file: File | null) => void;
  /** Dentro de un panel lateral: sin tarjeta ni título. */
  bare?: boolean;
}

/**
 * Dentro de un panel lateral no hace falta tarjeta ni título: el panel ya los pone. Con
 * `bare` se pinta solo el contenido.
 */
function Contenedor({ bare, className, children }: { bare: boolean; className?: string; children: ReactNode }) {
  return bare ? <div>{children}</div> : <Card className={className}>{children}</Card>;
}

/** El aviso de que el mes, que estaba conciliado, ha vuelto a incompleto. */
export function avisarReapertura(month: string): void {
  const nombre = MONTH_NAMES_ES[Number(month.slice(5, 7)) - 1] ?? month;
  toast.info(`${nombre} estaba conciliado: ha vuelto a «incompleto» para que lo revises y lo concilies otra vez.`);
}

export function MissingPanel({
  month,
  data,
  canManage,
  onReviewEntry,
  onResolveMovement,
  onMovementTicket,
  onOffBankPayment,
  bare = false,
}: MissingPanelProps) {
  const queryClient = useQueryClient();
  /** La fila que está subiendo: una cada vez, y la casilla de esa fila dice «Subiendo…». */
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const conciliado = data.close.status === 'conciliado';

  async function subir(clave: string, accion: () => Promise<{ reopened?: boolean }>, hecho: string) {
    setSubiendo(clave);
    try {
      const resultado = await accion();
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      toast.success(hecho);
      if (resultado.reopened) avisarReapertura(month);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido subir el fichero.');
    } finally {
      setSubiendo(null);
    }
  }

  // Con los gastos generales al final: a sus tickets también les puede faltar el papel.
  const inmuebles = conGenerales(data).filter((property) => property.gaps.length > 0);
  const banco = data.bank.movements.filter((movement) => movement.status === 'pendiente');
  const nada = inmuebles.length === 0 && banco.length === 0 && !data.bank.missingStatement;

  return (
    <Contenedor bare={bare} className={styles.missing}>
      {!bare && (
        <CardHeader
          title="Lo que falta"
          subtitle={
            nada
              ? undefined
              : 'Suelta cada ticket o justificante en su fila: así no hay que adivinar de qué es.'
          }
        />
      )}

      {nada ? (
        <p className={styles.missingNothing}>
          <Icon name="check" size={15} strokeWidth={2.4} /> No falta nada este mes.
        </p>
      ) : (
        <>
          {inmuebles.map((property) => (
            <section key={property.id} className={styles.missingGroup}>
              <h3 className={styles.missingTitle}>{property.name}</h3>
              <ul className={styles.missingList2}>
                {property.gaps.map((gap) => {
                  const clave = `${gap.reason}-${gap.entryId ?? gap.recurringExpenseId ?? gap.rentChargeId ?? gap.concept}`;
                  const cobro = gap.reason === 'sin_cobrar';
                  // En constantes: dentro de los callbacks de abajo, TypeScript ya no recuerda la
                  // comprobación de `gap.entryId` y obligaría a afirmarla con `!`.
                  const entryId = gap.entryId;
                  const fijoId = gap.recurringExpenseId;
                  const entry = entryId ? property.entries.find((e) => e.id === entryId) : undefined;
                  return (
                    <li key={clave} className={styles.missingRow} title={AYUDA[gap.reason]}>
                      <span className={cobro ? styles.missingBadgeIncome : styles.missingBadge}>
                        {ETIQUETA[gap.reason]}
                      </span>
                      <span className={styles.missingConcept}>{gap.concept}</span>
                      {gap.expectedAmount && (
                        <span className={cobro ? `${styles.missingAmount} ${styles.income}` : styles.missingAmount}>
                          {cobro ? formatEurCents(gap.expectedAmount) : `-${formatEurCents(gap.expectedAmount)}`}
                        </span>
                      )}
                      {canManage && (
                        <span className={styles.missingActions}>
                          {gap.reason === 'sin_apunte' && fijoId && (
                            <FileDrop
                              label="Soltar su ticket"
                              busy={subiendo === clave}
                              onFile={(file) =>
                                void subir(
                                  clave,
                                  () => api.uploadLedgerFile(month, property.id, file, undefined, fijoId),
                                  `Ticket de «${gap.concept}» subido. Repásalo y confírmalo.`,
                                )
                              }
                            />
                          )}
                          {gap.reason === 'sin_factura' && entryId && (
                            <FileDrop
                              label="Soltar su ticket"
                              busy={subiendo === clave}
                              onFile={(file) =>
                                void subir(
                                  clave,
                                  () => api.attachEntryDocument(entryId, file),
                                  `Ticket adjuntado a «${gap.concept}».`,
                                )
                              }
                            />
                          )}
                          {gap.reason === 'sin_confirmar' && entry && !conciliado && (
                            <button
                              type="button"
                              className={styles.missingButton}
                              onClick={() => onReviewEntry(entry, property.name)}
                            >
                              Revisar
                            </button>
                          )}
                          {cobro && (
                            <>
                              <FileDrop
                                label="Soltar justificante"
                                onFile={(file) => onOffBankPayment(gap, property.name, file)}
                              />
                              <button
                                type="button"
                                className={styles.missingButton}
                                onClick={() => onOffBankPayment(gap, property.name, null)}
                              >
                                Cobrado fuera del banco
                              </button>
                            </>
                          )}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {(banco.length > 0 || data.bank.missingStatement) && (
            <section className={styles.missingGroup}>
              <h3 className={styles.missingTitle}>Banco</h3>
              <ul className={styles.missingList2}>
                {data.bank.missingStatement && (
                  <li className={styles.missingRow}>
                    <span className={styles.missingBadge}>Sin extracto</span>
                    <span className={styles.missingConcept}>
                      El mes terminó y falta su extracto: súbelo con «Subir extracto».
                    </span>
                  </li>
                )}
                {banco.map((movement) => {
                  const ingreso = movement.kind === 'ingreso';
                  return (
                    <li key={movement.id} className={styles.missingRow}>
                      <span className={ingreso ? styles.missingBadgeIncome : styles.missingBadge}>
                        {ingreso ? 'Sin identificar' : 'Sin ticket'}
                      </span>
                      <span className={styles.missingConcept}>
                        {formatShortDate(movement.bookedAt)} · {movement.description}
                        {movement.aiDoubt && (
                          <span className={styles.missingDoubt}>
                            <span className={styles.iaDudaTag}>Duda</span> {movement.aiDoubt}
                          </span>
                        )}
                      </span>
                      <span className={ingreso ? `${styles.missingAmount} ${styles.income}` : styles.missingAmount}>
                        {formatEurCents(movement.amount)}
                      </span>
                      {canManage && !conciliado && (
                        <span className={styles.missingActions}>
                          {!ingreso && (
                            <FileDrop label="Soltar su ticket" onFile={(file) => onMovementTicket(movement, file)} />
                          )}
                          <button
                            type="button"
                            className={styles.missingButton}
                            onClick={() => onResolveMovement(movement)}
                          >
                            {movement.aiDoubt ? 'Resolver la duda' : ingreso ? '¿De quién es?' : 'Otra cosa'}
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </Contenedor>
  );
}
